import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  styled,
  CircularProgress,
  Grid,
  Paper,
  Avatar,
  Tooltip,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PeopleIcon from '@mui/icons-material/People';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { Client, InventoryItem, PublicClient, RouteGroup, Transportista } from '../models';
import RouteGroupsCRUD from './RouteGroupsCRUD';
import ClientList from './ClientList';
import OrdersCalendar from './OrdersCalendar';
import OrdersTable from './OrdersTable';
import InventoryManagement from './InventoryManagement';
import PreprogrammedOrders from './PreprogrammedOrders';
import TransportistaCRUD from './TransportistaCRUD';
import UnificationRoutes from './UnificationRoutes';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CustomRoutesUnionCRUD from './CustomRoutesUnionCRUD';
import CustomRoutesUnificationCRUD from './CustomRoutesUnificationCRUD';
import { Theme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import OperacionesDashboard from './OperacionesDashboard';
// import gitlabLogo from '../images/gitlabLogo.png';

const drawerWidth = 280;

interface AdminDashboardProps {
  defaultSection?: string;
  role?: string;
  transportistaCorreo?: string;
}

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: '#f4f7f9',
    paddingTop: theme.spacing(2),
    borderRight: '1px solid #e0e0e0',
    overflowX: 'hidden', // Prevent horizontal scrolling
  },
  '& .menu-scroll-container': {
    overflowY: 'auto',
    overflowX: 'hidden', // Hide horizontal scroll
    '&::-webkit-scrollbar': {
      display: 'none' // Hide scrollbar for Chrome, Safari, Opera
    },
    '-ms-overflow-style': 'none', // Hide scrollbar for IE and Edge
    'scrollbar-width': 'none' // Hide scrollbar for Firefox
  }
}));

const Logo = styled('img')({
  width: '180px',
  margin: '0 auto 30px',
  display: 'block',
  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
});

const StyledListItem = styled(ListItem)(({ theme }) => {
  return {
    borderRadius: '8px',
    margin: '4px 8px',
    transition: 'all 0.2s ease',
    '&.Mui-selected': {
      backgroundColor: 'rgba(0, 180, 216, 0.1)',
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: '#00b4d8',
        fontWeight: 600,
      },
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 180, 216, 0.08)',
      transform: 'translateX(5px)',
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: '#00b4d8',
      },
    },
  };
});

const NavItemText = styled(ListItemText)({
  '& .MuiListItemText-primary': {
    fontSize: '0.95rem',
    fontWeight: 500,
    color: '#333',
    transition: 'color 0.2s ease',
  },
});

const DividerText = styled(Typography)({
  padding: '16px 16px 8px',
  color: '#00b4d8',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: '1px',
});

const DividerTextDesk = styled(Typography)({
  padding: '16px 16px 8px',
  color: '#00b4d8',
  fontSize: '0.6rem',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: '1px',
});

const SubDividerTextDesk = styled(Typography)({
  padding: '8px 16px 4px',
  color: '#666',
  fontSize: '0.55rem',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  paddingLeft: '24px', // Indentación para mostrar jerarquía
});

const SubDividerText = styled(Typography)({
  padding: '8px 16px 4px',
  color: '#666',
  fontSize: '0.75rem',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  paddingLeft: '24px', // Indentación para mostrar jerarquía
});

const NavIcon = styled(ListItemIcon)({
  color: '#757575',
  minWidth: '40px',
  transition: 'color 0.2s ease',
});

const MainContent = styled(Box)(({ theme }) => {
  return {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f9fafb',
    minHeight: '100vh',
  };
});

const PageTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 'bold',
  marginBottom: '24px',
  color: '#00b4d8',
  borderBottom: '3px solid #00b4d8',
  paddingBottom: '12px',
  marginTop: '24px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
});

const UserProfileContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  marginBottom: '16px',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
});

const LogoutButton = styled(IconButton)({
  marginLeft: 'auto',
  color: '#00b4d8',
  '&:hover': {
    backgroundColor: 'rgba(0, 180, 216, 0.1)',
  },
});


const AdminDashboard: React.FC<AdminDashboardProps> = ({ defaultSection, role, transportistaCorreo }) => {
  const [selectedSection, setSelectedSection] = useState(defaultSection || 'calendar');
  const [clients, setClients] = useState<Client[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  // const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [publicClients, setPublicClients] = useState<PublicClient[]>([]);
  const [transportistas, setTransportistas] = useState<Transportista[]>([]);
  const [routeGroups, setRouteGroups] = useState<RouteGroup[]>([]);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [isDesk, setIsDesk] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleMenuIsDesk = () => {
    setIsDesk(!isDesk);
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
      toast.success('Sesión cerrada exitosamente');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Error al cerrar sesión');
    }
  };

  const fetchPublicClients = useCallback(async () => {
    try {
      const q = query(collection(db, 'publicClients'));
      const querySnapshot = await getDocs(q);
      const publicClientsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as PublicClient));
      setPublicClients(publicClientsList);
    } catch (error) {
      console.error('Error fetching public clients:', error);
      setError('Error al cargar los clientes públicos');
      toast.error('Error al cargar los clientes públicos');
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const q = query(collection(db, 'clients'));
      const querySnapshot = await getDocs(q);
      const clientsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client));
      setClients(clientsList);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Error al cargar los clientes');
      toast.error('Error al cargar los clientes');
    }
  }, []);

  const fetchInventoryItems = useCallback(async () => {
    try {
      const q = query(collection(db, 'inventoryItems'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as InventoryItem));
      setInventoryItems(items);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      toast.error('Error al cargar los items de inventario');
    }
  }, []);

  const fetchTransportistas = useCallback(async () => {
    try {
      const q = query(collection(db, 'transportistas'));
      const querySnapshot = await getDocs(q);
      const transportistasList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Transportista));
      setTransportistas(transportistasList);
    } catch (error) {
      console.error('Error fetching transportistas:', error);
      setError('Error al cargar los transportistas');
      toast.error('Error al cargar los transportistas');
    }
  }, []);

  const fetchRouteGroups = useCallback(async () => {
    try {
      const routeGroupsRef = collection(db, 'routeGroups');
      const querySnapshot = await getDocs(routeGroupsRef);
      const routeGroupsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as RouteGroup));
      setRouteGroups(routeGroupsData);
    } catch (error) {
      console.error('Error fetching routeGroups:', error);
      toast.error('Error al cargar los routeGroups');
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchClients(), fetchInventoryItems(), fetchPublicClients(), fetchTransportistas(), fetchRouteGroups()]);
      // setIsLoading(false);
    };
    fetchData();
  }, [fetchClients, fetchInventoryItems, fetchPublicClients, fetchTransportistas]);

  const handleUpdateInventoryItem = async (updatedItem: InventoryItem) => {
    try {
      const itemRef = doc(db, 'inventoryItems', updatedItem.id);
      await updateDoc(itemRef, {
        ...updatedItem,
        updatedAt: serverTimestamp()
      });
      toast.success('Item actualizado exitosamente');
      fetchInventoryItems();
    } catch (error) {
      console.error('Error updating inventory item:', error);
      toast.error('Error al actualizar el item');
    }
  };

  const handleDeleteInventoryItem = async (itemId: string) => {
    if (window.confirm('¿Está seguro de que desea eliminar este item?')) {
      try {
        await deleteDoc(doc(db, 'inventoryItems', itemId));
        toast.success('Item eliminado exitosamente');
        fetchInventoryItems();
      } catch (error) {
        console.error('Error deleting inventory item:', error);
        toast.error('Error al eliminar el item');
      }
    }
  };

  const handleAddTransportista = async (newItem: Omit<Transportista, 'id'>) => {
    try {
      await addDoc(collection(db, 'transportistas'), {
        ...newItem,
        createdAt: serverTimestamp(),
      });
      toast.success('Nuevo item agregado exitosamente');
      fetchInventoryItems();
    } catch (error) {
      console.error('Error adding new transportista item:', error);
      toast.error('Error al agregar el nuevo item');
    }
  };

  const getTransportistas = async () => {
    try {
      const transportistasRef = collection(db, 'transportistas');
      const querySnapshot = await getDocs(transportistasRef);
      const transportistasData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Transportista[];
      setTransportistas(transportistasData);
    } catch (error) {
      console.error('Error fetching transportistas:', error);
      toast.error('Error al cargar los transportistas');
    }
  };

  const handleAddInventoryItem = async (newItem: Omit<InventoryItem, 'id'>) => {
    try {
      await addDoc(collection(db, 'inventoryItems'), {
        ...newItem,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      toast.success('Nuevo item agregado exitosamente');
      fetchInventoryItems();
    } catch (error) {
      console.error('Error adding new inventory item:', error);
      toast.error('Error al agregar el nuevo item');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
      toast.success('Sesión cerrada exitosamente');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      toast.error('Error al cerrar sesión');
    }
    // finally {
    //   setIsLoading(false);
    // }
  };

  const handlePublicClientSubmit = async (client: PublicClient) => {
    try {
      await addDoc(collection(db, 'publicClients'), client);
      toast.success('Cliente público agregado exitosamente');
      fetchPublicClients();
    } catch (error) {
      console.error('Error adding public client:', error);
      toast.error('Error al agregar cliente público');
    }
  };

  const handlePublicClientUpdate = async (client: PublicClient) => {
    try {
      if (client.id) {
        await updateDoc(doc(db, 'publicClients', client.id), { ...client });
        toast.success('Cliente público actualizado exitosamente');
        fetchPublicClients();
      }
    } catch (error) {
      console.error('Error updating public client:', error);
      toast.error('Error al actualizar cliente público');
    }
  };

  const handlePublicClientDelete = async (clientId: string) => {
    try {
      await deleteDoc(doc(db, 'publicClients', clientId));
      toast.success('Cliente público eliminado exitosamente');
      fetchPublicClients();
    } catch (error) {
      console.error('Error deleting public client:', error);
      toast.error('Error al eliminar cliente público');
    }
  };

  const handleClientSubmit = async (client: Client) => {
    try {
      await addDoc(collection(db, 'clients'), client);
      toast.success('Cliente agregado exitosamente');
      fetchClients();
    } catch (error) {
      console.error('Error adding client:', error);
      toast.error('Error al agregar cliente');
    }
  };

  const handleClientUpdate = async (client: Client) => {
    try {
      if (client.id) {
        await updateDoc(doc(db, 'clients', client.id), { ...client });
        toast.success('Cliente actualizado exitosamente');
        fetchClients();
      }
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Error al actualizar cliente');
    }
  };

  const handleClientDelete = async (clientId: string) => {
    try {
      await deleteDoc(doc(db, 'clients', clientId));
      toast.success('Cliente eliminado exitosamente');
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Error al eliminar cliente');
    }
  };

  const handleViewTransportistaDetails = (row: Transportista) => {
    // Implementar la lógica para ver detalles del transportista
  };

  const handleEditTransportista = async (row: Transportista) => {
    try {
      if (row.id) {
        await updateDoc(doc(db, 'transportistas', row.id), { ...row });
        toast.success('Cliente actualizado exitosamente');
        fetchClients();
      }
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Error al actualizar cliente');
    }
  };

  const handleDeleteTransportista = async (id?: string) => {
    if (id) {
      try {
        await deleteDoc(doc(db, 'transportistas', id));
        toast.success('Transportista eliminado exitosamente');
        fetchTransportistas();
      } catch (error) {
        console.error('Error deleting transportista:', error);
        toast.error('Error al eliminar el transportista');
      }
    }
  };

  const renderContent = () => {
    switch (selectedSection) {
      case 'home':
        return (
          <Box>
            <PageTitle>Panel de Administración</PageTitle>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">Clientes</Typography>
                  <Typography variant="h4" color="textPrimary">{clients.length}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">Inventario</Typography>
                  <Typography variant="h4" color="textPrimary">{inventoryItems.length}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">Transportistas</Typography>
                  <Typography variant="h4" color="textPrimary">{transportistas.length}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );
      case 'clients':
        return <ClientList
          clients={clients}
          publicClients={publicClients}
          onUpdate={handleClientUpdate}
          onDelete={handleClientDelete}
          onAddNew={handleClientSubmit}
          onUpdatePublicClient={handlePublicClientUpdate}
          onAddNewPublicClient={handlePublicClientSubmit}
          onDeletePublicClient={handlePublicClientDelete}
        />;
      case 'orders':
        return <OrdersTable />;
      case 'calendar':
        return <OrdersCalendar />;
      case 'inventory':
        return <InventoryManagement
          inventoryItems={inventoryItems}
          onUpdateItem={handleUpdateInventoryItem}
          onDeleteItem={handleDeleteInventoryItem}
          onAddItem={handleAddInventoryItem}
        />;
      case 'routes':
        return <RouteGroupsCRUD />;
      case 'pRoutes':
        return <CustomRoutesUnionCRUD />;
      case 'pRoutesUni':
        return <CustomRoutesUnificationCRUD setSelectedSection={setSelectedSection} />
      case 'unificationRoutes':
        return <UnificationRoutes />;
      case 'transportistas':
        return <TransportistaCRUD
          routeGroups={routeGroups}
          transportistas={transportistas}
          handleViewDetails={handleViewTransportistaDetails}
          handleEditItem={handleEditTransportista}
          onAddItem={handleAddTransportista}
          handleDeleteItem={handleDeleteTransportista}
          getData={getTransportistas}
          setSelectedSection={setSelectedSection}
        />;
      case 'operaciones':
        return transportistaCorreo ? (
          <OperacionesDashboard
            transportistaCorreo={transportistaCorreo}
            inventoryItems={inventoryItems}
            handleLogout={handleLogout}
          />
        ) : null;
      case 'preprogrammed-orders':
        return <PreprogrammedOrders />;
      default:
        return null;
    }
  };
  return (
    <Box sx={{ display: 'flex' }}>
      {role == "admin" ? (isMobile ? (
        <>
          <IconButton onClick={toggleMenu} sx={{ position: 'absolute', top: 16, left: 16 }}>
            <MenuIcon />
          </IconButton>
          <Drawer anchor="left" open={menuOpen} onClose={toggleMenu}>
            <Box className="menu-scroll-container" sx={{ marginTop: menuOpen ? 2 : 0, width: 240 }}>
              <Logo onClick={toggleMenu} src="https://ollinadmin.web.app/static/media/ollin_Logo.e2ef52aceaf443447897.png" alt="Ollin Logo" />
              {/* <Logo src="/images/gitlabLogo.png" alt="GitLab Logo" /> */}
              <List sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box>
                  <StyledListItem
                    selected={selectedSection === 'calendar'}
                    onClick={() => setSelectedSection('calendar')}
                  >
                    <NavIcon><EventNoteIcon /></NavIcon>
                    <NavItemText primary="Calendario" />
                  </StyledListItem>

                  <DividerText>Gestión de Pedidos</DividerText>
                  <StyledListItem
                    selected={selectedSection === 'orders'}
                    onClick={() => setSelectedSection('orders')}
                  >
                    <NavIcon><ShoppingCartIcon /></NavIcon>
                    <NavItemText primary="Programados" />
                  </StyledListItem>

                  <StyledListItem
                    selected={selectedSection === 'preprogrammed-orders'}
                    onClick={() => setSelectedSection('preprogrammed-orders')}
                  >
                    <NavIcon><CheckCircleIcon /></NavIcon>
                    <NavItemText primary="Reprogramados" />
                  </StyledListItem>

                  <DividerText>Operaciones</DividerText>
                  <SubDividerText>Gestión de Clientes</SubDividerText>
                  <StyledListItem
                    selected={selectedSection === 'clients'}
                    onClick={() => setSelectedSection('clients')}
                  >
                    <NavIcon><PeopleIcon /></NavIcon>
                    <NavItemText primary="Clientes" />
                  </StyledListItem>

                  <SubDividerText>Gestión de Inventario</SubDividerText>
                  <StyledListItem
                    selected={selectedSection === 'inventory'}
                    onClick={() => setSelectedSection('inventory')}
                  >
                    <NavIcon><Inventory2Icon /></NavIcon>
                    <NavItemText primary="Inventario" />
                  </StyledListItem>

                  <SubDividerText>Gestión de Rutas</SubDividerText>
                  <StyledListItem
                    selected={selectedSection === 'routes'}
                    onClick={() => setSelectedSection('routes')}
                  >
                    <NavIcon><LocalShippingIcon /></NavIcon>
                    <NavItemText primary="Automatización" />
                  </StyledListItem>
                  {/* <StyledListItem
                    selected={selectedSection === 'pRoutes'}
                    onClick={() => setSelectedSection('pRoutes')}
                  >
                    <NavIcon><AssignmentIcon /></NavIcon>
                    <NavItemText primary="Personalización" />
                  </StyledListItem>              
                   */}
                  <SubDividerText>Gestión de Unificación</SubDividerText>
                  <StyledListItem
                    selected={selectedSection === 'pRoutesUni'}
                    onClick={() => setSelectedSection('pRoutesUni')}
                  >
                    <NavIcon><DashboardIcon /></NavIcon>
                    <NavItemText primary="Edición" />
                  </StyledListItem>
                  <StyledListItem
                    selected={selectedSection === 'unificationRoutes'}
                    onClick={() => setSelectedSection('unificationRoutes')}
                  >
                    <NavIcon><LocalMallIcon /></NavIcon>
                    <NavItemText primary="Creación" />
                  </StyledListItem>

                  <SubDividerText>Gestión de Transportistas</SubDividerText>
                  <StyledListItem
                    selected={selectedSection === 'transportistas'}
                    onClick={() => setSelectedSection('transportistas')}
                  >
                    <NavIcon><DriveEtaIcon /></NavIcon>
                    <NavItemText primary="Transportistas" />
                  </StyledListItem>
                  {/* <StyledListItem
                    selected={selectedSection === 'operaciones'}
                    onClick={() => setSelectedSection('operaciones')}
                  >
                    <NavIcon><SettingsIcon /></NavIcon>
                    <NavItemText primary="Operaciones" />
                  </StyledListItem> */}
                </Box>

                <Box sx={{ marginTop: 'auto', paddingBottom: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title="Cerrar Sesión" placement="right" arrow>
                    <LogoutButton onClick={handleLogout}>
                      <ExitToAppIcon />
                    </LogoutButton>
                  </Tooltip>
                </Box>
              </List>
            </Box>
          </Drawer>
        </>
      ) : (isDesk ? (
        <>
          <IconButton onClick={toggleMenuIsDesk} sx={{ position: 'absolute', top: 16, left: 16 }}>
            <MenuIcon />
          </IconButton>
          <Drawer anchor="left"
            open={isDesk}
            onClose={toggleMenuIsDesk}
            hideBackdrop={true}
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                position: 'relative',
                width: 95,
                boxSizing: 'border-box',
                overflowX: 'hidden',
              }
            }}>
            <StyledDrawer
              variant="permanent"
              sx={{
                width: 100,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                  width: 100,
                  boxSizing: 'border-box',
                  overflowX: 'hidden',
                }
              }}
            >
              <Box className="menu-scroll-container">
                <Logo sx={{
                  width: '100%',
                  margin: '0 auto 2px',
                  display: 'block',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                }} onClick={toggleMenuIsDesk} src="data:image/png;base64,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" alt="Ollin Logo" />
                {/* <Logo src="/images/gitlabLogo.png" alt="GitLab Logo" /> */}
                <List sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box>
                    <StyledListItem
                      selected={selectedSection === 'calendar'}
                      onClick={() => setSelectedSection('calendar')}
                    >
                      <NavIcon><EventNoteIcon sx={{ fontSize: '33px' }} /></NavIcon>
                    </StyledListItem>

                    <DividerTextDesk>Gestión de Pedidos</DividerTextDesk>
                    <StyledListItem
                      selected={selectedSection === 'orders'}
                      onClick={() => setSelectedSection('orders')}
                    >
                      <NavIcon><ShoppingCartIcon sx={{ fontSize: '33px' }} /></NavIcon>
                    </StyledListItem>


                    <Tooltip title="Pedidos Preprogramados" placement="right" arrow>
                      <StyledListItem
                        selected={selectedSection === 'preprogrammed-orders'}
                        onClick={() => setSelectedSection('preprogrammed-orders')}
                      >
                        <NavIcon><CheckCircleIcon sx={{ fontSize: '33px' }} /></NavIcon>
                      </StyledListItem>
                    </Tooltip>

                    <DividerTextDesk>Operaciones</DividerTextDesk>
                    <SubDividerTextDesk>Gestión de Clientes</SubDividerTextDesk>
                    <StyledListItem
                      selected={selectedSection === 'clients'}
                      onClick={() => setSelectedSection('clients')}
                    >
                      <NavIcon><PeopleIcon sx={{ fontSize: '33px' }} /></NavIcon>
                    </StyledListItem>

                    <SubDividerTextDesk>Gestión de Inventario</SubDividerTextDesk>
                    <StyledListItem
                      selected={selectedSection === 'inventory'}
                      onClick={() => setSelectedSection('inventory')}
                    >
                      <NavIcon><Inventory2Icon sx={{ fontSize: '33px' }} /></NavIcon>
                    </StyledListItem>

                    <SubDividerTextDesk>Gestión de Rutas</SubDividerTextDesk>
                    <StyledListItem
                      selected={selectedSection === 'routes'}
                      onClick={() => setSelectedSection('routes')}
                    >
                      <NavIcon><LocalShippingIcon sx={{ fontSize: '33px' }} /></NavIcon>
                    </StyledListItem>
                    {/* <StyledListItem
                  selected={selectedSection === 'pRoutes'}
                  onClick={() => setSelectedSection('pRoutes')}
                >
                  <NavIcon><AssignmentIcon /></NavIcon>
                  <NavItemText primary="Personalización" />
                </StyledListItem>              
                 */}
                    <SubDividerTextDesk>Gestión de Unificación</SubDividerTextDesk>
                    <StyledListItem
                      selected={selectedSection === 'pRoutesUni'}
                      onClick={() => setSelectedSection('pRoutesUni')}
                    >
                      <NavIcon><DashboardIcon sx={{ fontSize: '33px' }} /></NavIcon>
                    </StyledListItem>
                    <StyledListItem
                      selected={selectedSection === 'unificationRoutes'}
                      onClick={() => setSelectedSection('unificationRoutes')}
                    >
                      <NavIcon><LocalMallIcon sx={{ fontSize: '33px' }} /></NavIcon>
                    </StyledListItem>

                    <SubDividerTextDesk>Gestión de Transportistas</SubDividerTextDesk>
                    <StyledListItem
                      selected={selectedSection === 'transportistas'}
                      onClick={() => setSelectedSection('transportistas')}
                    >
                      <NavIcon><DriveEtaIcon sx={{ fontSize: '33px' }} /></NavIcon>
                    </StyledListItem>
                    {/* <StyledListItem
                  selected={selectedSection === 'operaciones'}
                  onClick={() => setSelectedSection('operaciones')}
                >
                  <NavIcon><SettingsIcon /></NavIcon>
                  <NavItemText primary="Operaciones" />
                </StyledListItem> */}
                  </Box>

                  <Box sx={{ marginTop: 'auto', paddingBottom: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Cerrar Sesión" placement="right" arrow>
                      <LogoutButton onClick={handleLogout}>
                        <ExitToAppIcon />
                      </LogoutButton>
                    </Tooltip>
                  </Box>
                </List>
              </Box>
            </StyledDrawer>
          </Drawer>
        </>
      ) : (
        <StyledDrawer
          variant="permanent"
          sx={{
            width: 240,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: 240,
              boxSizing: 'border-box',
              overflowX: 'hidden',
            }
          }}
        >
          <Box className="menu-scroll-container">
            <Logo onClick={toggleMenuIsDesk} src="https://ollinadmin.web.app/static/media/ollin_Logo.e2ef52aceaf443447897.png" alt="Ollin Logo" />
            {/* <Logo src="/images/gitlabLogo.png" alt="GitLab Logo" /> */}
            <List sx={{ hheight: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box>
                <StyledListItem
                  selected={selectedSection === 'calendar'}
                  onClick={() => setSelectedSection('calendar')}
                >
                  <NavIcon><EventNoteIcon /></NavIcon>
                  <NavItemText primary="Calendario" />
                </StyledListItem>

                <DividerText>Gestión de Pedidos</DividerText>
                <StyledListItem
                  selected={selectedSection === 'orders'}
                  onClick={() => setSelectedSection('orders')}
                >
                  <NavIcon><ShoppingCartIcon /></NavIcon>
                  <NavItemText primary="Programados" />
                </StyledListItem>
                <StyledListItem
                  selected={selectedSection === 'preprogrammed-orders'}
                  onClick={() => setSelectedSection('preprogrammed-orders')}
                >
                  <NavIcon><CheckCircleIcon /></NavIcon>
                  <NavItemText primary="Reprogramados" />
                </StyledListItem>

                <DividerText>Operaciones</DividerText>
                <SubDividerText>Gestión de Clientes</SubDividerText>
                <StyledListItem
                  selected={selectedSection === 'clients'}
                  onClick={() => setSelectedSection('clients')}
                >
                  <NavIcon><PeopleIcon /></NavIcon>
                  <NavItemText primary="Clientes" />
                </StyledListItem>
                <SubDividerText>Gestión de Inventario</SubDividerText>
                <StyledListItem
                  selected={selectedSection === 'inventory'}
                  onClick={() => setSelectedSection('inventory')}
                >
                  <NavIcon><Inventory2Icon /></NavIcon>
                  <NavItemText primary="Inventario" />
                </StyledListItem>

                <SubDividerText>Gestión de Rutas</SubDividerText>
                <StyledListItem
                  selected={selectedSection === 'routes'}
                  onClick={() => setSelectedSection('routes')}
                >
                  <NavIcon><LocalShippingIcon /></NavIcon>
                  <NavItemText primary="Automatización" />
                </StyledListItem>
                {/* <StyledListItem
                selected={selectedSection === 'pRoutes'}
                onClick={() => setSelectedSection('pRoutes')}
              >
                <NavIcon><AssignmentIcon /></NavIcon>
                <NavItemText primary="Personalización" />
              </StyledListItem>              
               */}
                <SubDividerText>Gestión de Unificación</SubDividerText>
                <StyledListItem
                  selected={selectedSection === 'pRoutesUni'}
                  onClick={() => setSelectedSection('pRoutesUni')}
                >
                  <NavIcon><DashboardIcon /></NavIcon>
                  <NavItemText primary="Edición" />
                </StyledListItem>
                <StyledListItem
                  selected={selectedSection === 'unificationRoutes'}
                  onClick={() => setSelectedSection('unificationRoutes')}
                >
                  <NavIcon><LocalMallIcon /></NavIcon>
                  <NavItemText primary="Creación" />
                </StyledListItem>

                <SubDividerText>Gestión de Transportistas</SubDividerText>
                <StyledListItem
                  selected={selectedSection === 'transportistas'}
                  onClick={() => setSelectedSection('transportistas')}
                >
                  <NavIcon><DriveEtaIcon /></NavIcon>
                  <NavItemText primary="Transportistas" />
                </StyledListItem>
                {/* <StyledListItem
                selected={selectedSection === 'operaciones'}
                onClick={() => setSelectedSection('operaciones')}
              >
                <NavIcon><SettingsIcon /></NavIcon>
                <NavItemText primary="Operaciones" />
              </StyledListItem> */}
              </Box>

              <Box sx={{ marginTop: 'auto', paddingBottom: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Cerrar Sesión" placement="right" arrow>
                  <LogoutButton onClick={handleLogout}>
                    <ExitToAppIcon />
                  </LogoutButton>
                </Tooltip>
              </Box>
            </List>
          </Box>
        </StyledDrawer>

      ))) : null}

      {/* <MainContent>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100vh"
          >
            <CircularProgress color="primary" />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          renderContent()
        )}
      </MainContent> */}
      <MainContent>
        {(
          renderContent()
        )}
      </MainContent>
    </Box>
  );

};

export default AdminDashboard;